import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getChatControlData,
  setChatDialogOpen,
  setVideoChatOpen,
} from 'components/ChatDialog/store/slices/chatSlice';
import { setThreadWithUsers } from 'components/ChatDialog/store/thunks/loadThreadUsers';
import { syncPushSubscription } from 'utils/pushSubscription';

// How long a push's thread may take to arrive in the store after a cold start
// before the chat is opened without it.
const THREAD_WAIT_MS = 15000;

// Notifications that open their chat thread when clicked: a message, a call
// (into its video view), and what became of a call.
const THREAD_NOTIFICATION_TYPES = [
  'chat-message',
  'video-call',
  'missed-call',
  'call-answered',
];

const whenThreadLoaded = (store, threadId) =>
  new Promise((resolve) => {
    const find = () => getChatControlData(store.getState()).threads[threadId];

    const found = find();
    if (found) {
      resolve(found);
      return;
    }

    let timer = null;
    const unsubscribe = store.subscribe(() => {
      const thread = find();
      if (thread) {
        clearTimeout(timer);
        unsubscribe();
        resolve(thread);
      }
    });
    timer = setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, THREAD_WAIT_MS);
  });

/**
 * Browser push, app-wide: keeps this browser's subscription current, and
 * opens what a push notification was about when it is clicked, whether a
 * Lixani window was already open (a service worker message) or the click
 * opened one (the `chatThread` / `videoCall` URL parameters).
 *
 * Mounted once, next to useChatNotifications.
 */
export const usePushNotifications = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const history = useHistory();
  const { i18n } = useTranslation();
  const user = useSelector((state) => state.root.user);
  const userId = user?.id;

  // On every start, and again when the language changes, since the push text
  // is written in the language the subscription carries.
  useEffect(() => {
    if (userId) {
      syncPushSubscription();
    }
  }, [userId, i18n.language]);

  useEffect(() => {
    const openThread = async (threadId, videoCall) => {
      const thread = await whenThreadLoaded(store, threadId);
      if (thread) {
        dispatch(setThreadWithUsers({ thread }));
      }
      dispatch(setChatDialogOpen(true));
      if (videoCall && thread) {
        dispatch(setVideoChatOpen(true));
      }
    };

    window.__pushNotificationClickHandler = (data) => {
      if (
        THREAD_NOTIFICATION_TYPES.includes(data.notificationType) &&
        data.threadId
      ) {
        openThread(data.threadId, data.notificationType === 'video-call');
      } else if (data.openUrl) {
        history.push(data.openUrl);
      }
    };

    // A click that opened this window: the parameters name the thread.
    const params = new URLSearchParams(window.location.search);
    const threadId = params.get('chatThread');
    if (threadId) {
      const videoCall = params.get('videoCall') === '1';
      params.delete('chatThread');
      params.delete('videoCall');
      const search = params.toString();
      history.replace(
        `${window.location.pathname}${search ? `?${search}` : ''}`
      );
      openThread(threadId, videoCall);
    }

    return () => {
      window.__pushNotificationClickHandler = null;
    };
  }, [dispatch, store, history]);
};
